import React from 'react';
import {
  Shield,
  ArrowLeft,
  Lock,
  Eye,
  Database,
  Cpu,
  Server,
  UserCheck,
  AlertCircle,
  FileCheck,
  Calendar,
  Sparkles,
} from 'lucide-react';

interface PrivacyNoticeViewProps {
  onBack: () => void;
}

export const PrivacyNoticeView: React.FC<PrivacyNoticeViewProps> = ({ onBack }) => {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-8 text-[#FAFAFA]">
      {/* Top Navigation Bar */}
      <div className="flex items-center justify-between gap-4 border-b border-[#262626] pb-4">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-[#141414] border border-[#404040] hover:bg-[#1C1C1C] text-xs font-semibold text-[#FAFAFA] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back</span>
        </button>
        <div className="flex items-center gap-2 text-xs font-mono text-[#A3A3A3]">
          <Calendar className="w-3.5 h-3.5 text-[#A3A3A3]" />
          <span>Effective Date: [EFFECTIVE DATE: October 31, 2026]</span>
        </div>
      </div>

      {/* Hero Header */}
      <div className="space-y-3">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
          <Shield className="w-3.5 h-3.5" />
          <span>Privacy &amp; Data Transparency</span>
        </div>
        <h1 className="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
          Privacy Notice
        </h1>
        <p className="text-sm sm:text-base text-[#A3A3A3] leading-relaxed">
          This Privacy Notice clearly explains what information Quick!Bible collects, how it is used, and how your data is handled.
        </p>
      </div>

      {/* Main Content Sections */}
      <div className="space-y-6 text-sm leading-relaxed text-[#D4D4D4]">
        {/* Section 1: Data Controller / Who is Responsible */}
        <section className="p-5 sm:p-6 rounded-2xl bg-[#141414] border border-[#262626] space-y-3">
          <h2 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
            <span className="w-6 h-6 rounded-lg bg-emerald-600/20 text-emerald-400 flex items-center justify-center text-xs font-mono">1</span>
            Who is Responsible for Your Data
          </h2>
          <p>
            Quick!Bible (<a href="https://quickibible.com" className="text-[#A3A3A3] hover:underline">https://quickibible.com</a>) is owned and operated by <strong>Adonis Melencion</strong> as a personal hobby project based in the Philippines.
          </p>
          <div className="p-3.5 rounded-xl bg-[#141414] border border-[#262626] space-y-1 text-xs font-mono text-[#D4D4D4]">
            <div><strong>Responsible Individual:</strong> Adonis Melencion (Sole Developer &amp; Owner)</div>
            <div><strong>Platform:</strong> Quick!Bible</div>
            <div><strong>Website:</strong> https://quickibible.com</div>
            <div className="text-amber-400 font-semibold">
              <strong>Contact Email:</strong>{' '}
              <a href="mailto:adonizchoy@gmail.com" className="underline hover:text-amber-300 transition-colors">
                adonizchoy@gmail.com
              </a>
            </div>
          </div>
        </section>

        {/* Section 2: Information Collected */}
        <section className="p-5 sm:p-6 rounded-2xl bg-[#141414] border border-[#262626] space-y-4">
          <h2 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
            <span className="w-6 h-6 rounded-lg bg-emerald-600/20 text-emerald-400 flex items-center justify-center text-xs font-mono">2</span>
            Information We Actually Collect
          </h2>
          <p>
            We only collect information directly necessary to provide and operate the Quick!Bible gameplay, leaderboards, and user profiles. Specifically:
          </p>

          <div className="space-y-3">
            <div className="p-3.5 rounded-xl bg-[#141414] border border-[#262626]">
              <h3 className="text-xs font-bold uppercase tracking-wider text-[#A3A3A3] mb-1">
                A. Account &amp; Registration Information
              </h3>
              <p className="text-xs text-[#D4D4D4]">
                When you create a scholar account (via Supabase Auth email/password or Google Sign-In), we collect your <strong>chosen username</strong>, <strong>display name</strong>, and <strong>email address</strong>. Passwords are encrypted and managed directly by Supabase Auth; our application server never receives or stores plain-text passwords.
              </p>
            </div>

            <div className="p-3.5 rounded-xl bg-[#141414] border border-[#262626]">
              <h3 className="text-xs font-bold uppercase tracking-wider text-[#A3A3A3] mb-1">
                B. Profile &amp; Preferences
              </h3>
              <p className="text-xs text-[#D4D4D4]">
                You may optionally provide or configure: country selection, language preference (English, Tagalog, Cebuano, Korean, Simplified/Traditional Chinese, Spanish), dark/light theme, custom bio, favorite Bible verse, church/organization affiliation, and neural audio narration settings (voice persona, speed, sound effects, music volume).
              </p>
            </div>

            <div className="p-3.5 rounded-xl bg-[#141414] border border-[#262626]">
              <h3 className="text-xs font-bold uppercase tracking-wider text-[#A3A3A3] mb-1">
                C. Gameplay, Score &amp; Tournament Statistics
              </h3>
              <p className="text-xs text-[#D4D4D4]">
                To maintain global rankings and user achievements, our database records: answers submitted during quiz sessions, question correctness, response time (in milliseconds, used for authoritative speed bonuses), total accumulated points, win streaks, daily challenge dates, and unlocked achievement codes.
              </p>
            </div>

            <div className="p-3.5 rounded-xl bg-[#141414] border border-[#262626]">
              <h3 className="text-xs font-bold uppercase tracking-wider text-[#A3A3A3] mb-1">
                D. Real-Time Multiplayer Session Data
              </h3>
              <p className="text-xs text-[#D4D4D4]">
                During multiplayer matchmaking and LIVE event rooms, temporary WebSockets (via Socket.IO) communicate active session PINs, locked answer selections, and current round scores between connected players and the host auditorium display.
              </p>
            </div>

            <div className="p-3.5 rounded-xl bg-[#141414] border border-[#262626]">
              <h3 className="text-xs font-bold uppercase tracking-wider text-[#A3A3A3] mb-1">
                E. Local Storage &amp; Technical Logs
              </h3>
              <p className="text-xs text-[#D4D4D4]">
                Quick!Bible uses browser <code>localStorage</code> to store an anonymous scholar identifier (<code>qb_scholar_uid</code>), selected language (<code>qb_language</code>), active theme (<code>qb_theme</code>), and terms acceptance status so you do not have to reconfigure them each visit. Web server infrastructure (Render) automatically logs standard technical metadata (IP address, user-agent, request timestamp) for security monitoring and operational diagnosis.
              </p>
            </div>
          </div>
        </section>

        {/* Section 3: Purposes of Processing */}
        <section className="p-5 sm:p-6 rounded-2xl bg-[#141414] border border-[#262626] space-y-3">
          <h2 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
            <span className="w-6 h-6 rounded-lg bg-emerald-600/20 text-emerald-400 flex items-center justify-center text-xs font-mono">3</span>
            Purposes of Data Processing
          </h2>
          <p>We process collected data exclusively for the following purposes:</p>
          <ul className="list-disc list-inside space-y-1.5 pl-2 text-[#D4D4D4]">
            <li>Operating your player account, authentication, and secure login;</li>
            <li>Tracking quiz progress, XP levels, biblical category mastery, and achievements;</li>
            <li>Calculating authoritative scores, speed bonuses, and country/global leaderboards;</li>
            <li>Synchronizing real-time multiplayer lobbies and auditorium LIVE events;</li>
            <li>Detecting and mitigating cheating, automated bots, or service disruption;</li>
            <li>Responding to user inquiries or support requests.</li>
          </ul>
        </section>

        {/* Section 4: Artificial Intelligence & Gemini Disclosure */}
        <section className="p-5 sm:p-6 rounded-2xl bg-[#141414] border border-[#262626] space-y-3">
          <h2 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
            <span className="w-6 h-6 rounded-lg bg-emerald-600/20 text-emerald-400 flex items-center justify-center text-xs font-mono">4</span>
            Artificial Intelligence &amp; Google Gemini API Disclosure
          </h2>
          <div className="p-4 rounded-xl bg-[#141414] border border-[#525252] space-y-2">
            <div className="flex items-center gap-2 text-[#FAFAFA] font-semibold text-xs">
              <Sparkles className="w-4 h-4 text-[#A3A3A3] shrink-0" />
              <span>Accurate Disclosure of Real AI Usage</span>
            </div>
            <p className="text-xs text-[#D4D4D4]">
              Quick!Bible integrates Google&apos;s <strong>Gemini API (gemini-3.8-flash)</strong> and <strong>Gemini TTS (gemini-3.8-flash-lite-tts)</strong> strictly on the backend server for two specific features:
            </p>
            <ol className="list-decimal list-inside space-y-1 text-xs text-[#D4D4D4] pl-1">
              <li>Generating canonical, verifiable biblical trivia questions across varied categories and difficulty levels; and</li>
              <li>Synthesizing neural voice narration of Scripture questions and references.</li>
            </ol>
            <p className="text-xs text-emerald-300 font-semibold pt-1">
              ✓ Privacy Guarantee: Quick!Bible DOES NOT send your personal user information (such as your name, email address, password, or user profile) to the Gemini API. Only biblical topics, difficulty parameters, and Scripture question texts are sent to the AI model.
            </p>
          </div>
        </section>

        {/* Section 5: Real Third-Party Providers & Data Sharing */}
        <section className="p-5 sm:p-6 rounded-2xl bg-[#141414] border border-[#262626] space-y-3">
          <h2 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
            <span className="w-6 h-6 rounded-lg bg-emerald-600/20 text-emerald-400 flex items-center justify-center text-xs font-mono">5</span>
            Third-Party Providers &amp; Infrastructure
          </h2>
          <p>
            Quick!Bible does not sell, rent, or trade your personal information. We rely on the following third-party infrastructure services to host and operate the platform:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
            <div className="p-3 rounded-xl bg-[#141414] border border-[#262626]">
              <div className="font-semibold text-white mb-1">Supabase (PostgreSQL &amp; Auth)</div>
              <p className="text-[#A3A3A3]">Provides secure user authentication, password management, and database storage for profiles.</p>
            </div>
            <div className="p-3 rounded-xl bg-[#141414] border border-[#262626]">
              <div className="font-semibold text-white mb-1">Firebase (Google Auth)</div>
              <p className="text-[#A3A3A3]">Enables optional Google Sign-In authentication popup and ID token verification.</p>
            </div>
            <div className="p-3 rounded-xl bg-[#141414] border border-[#262626]">
              <div className="font-semibold text-white mb-1">Render (Hosting &amp; Server)</div>
              <p className="text-[#A3A3A3]">Hosts the Node.js application server and serves the web application at quickibible.com.</p>
            </div>
            <div className="p-3 rounded-xl bg-[#141414] border border-[#262626]">
              <div className="font-semibold text-white mb-1">Google Cloud (Gemini Engine)</div>
              <p className="text-[#A3A3A3]">Processes automated Scripture question generation and neural TTS audio synthesis.</p>
            </div>
          </div>
        </section>

        {/* Section 6: Data Retention */}
        <section className="p-5 sm:p-6 rounded-2xl bg-[#141414] border border-[#262626] space-y-3">
          <h2 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
            <span className="w-6 h-6 rounded-lg bg-emerald-600/20 text-emerald-400 flex items-center justify-center text-xs font-mono">6</span>
            Data Retention &amp; Deletion Process
          </h2>
          <p>
            Account details, XP progression, and gameplay scores are retained for as long as your account remains active in the database to preserve your achievements, statistics, and leaderboard rankings across gaming sessions.
          </p>
          <div className="p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/20 text-xs text-amber-300">
            <strong>Placeholder Notice:</strong> [RETENTION PERIOD / DELETION PROCESS REQUIRED] — Specific archival retention limits and automated deletion workflows are currently defined by active account status; users may request manual account deletion as described below.
          </div>
        </section>

        {/* Section 7: User Rights & Data Requests */}
        <section className="p-5 sm:p-6 rounded-2xl bg-[#141414] border border-[#262626] space-y-3">
          <h2 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
            <span className="w-6 h-6 rounded-lg bg-emerald-600/20 text-emerald-400 flex items-center justify-center text-xs font-mono">7</span>
            Your Data Rights &amp; Access Requests
          </h2>
          <p>You have the following rights regarding your personal information:</p>
          <ul className="list-disc list-inside space-y-1.5 pl-2 text-[#D4D4D4]">
            <li><strong>Access &amp; View:</strong> You can view your account profile, stats, unlocked badges, and preferences at any time in the Profile &amp; Settings screen.</li>
            <li><strong>Correction &amp; Update:</strong> You can update your display name, username, country, language, bio, and preferences directly in the app.</li>
            <li><strong>Account Deletion:</strong> You may request the deletion of your account and associated database records by contacting the owner at <a href="mailto:adonizchoy@gmail.com" className="text-amber-400 font-mono underline hover:text-amber-300 transition-colors">adonizchoy@gmail.com</a>.</li>
          </ul>
        </section>

        {/* Section 8: Security */}
        <section className="p-5 sm:p-6 rounded-2xl bg-[#141414] border border-[#262626] space-y-3">
          <h2 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
            <span className="w-6 h-6 rounded-lg bg-emerald-600/20 text-emerald-400 flex items-center justify-center text-xs font-mono">8</span>
            Security Measures
          </h2>
          <p>
            Quick!Bible implements reasonable technical safeguards appropriate for a personal hobby project, including HTTPS/TLS encryption for all data in transit, token-based API authentication, and salted password hashing through Supabase Auth.
          </p>
          <p className="text-xs text-[#A3A3A3]">
            Please understand that no method of transmission over the Internet or electronic storage is 100% secure, and absolute security cannot be promised or guaranteed.
          </p>
        </section>

        {/* Section 9: Children & Minors */}
        <section className="p-5 sm:p-6 rounded-2xl bg-[#141414] border border-[#262626] space-y-3">
          <h2 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
            <span className="w-6 h-6 rounded-lg bg-emerald-600/20 text-emerald-400 flex items-center justify-center text-xs font-mono">9</span>
            Children &amp; Minors Policy
          </h2>
          <p>
            Quick!Bible is designed for general audiences and family Bible study.
          </p>
          <div className="p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/20 text-xs text-amber-300">
            <strong>Placeholder Notice:</strong> [AGE POLICY REQUIRED] — Quick!Bible has not established an independent age verification or verifiable parental consent mechanism. Parents and legal guardians are advised to monitor minor internet use. If you believe a minor has provided personal information without consent, contact [OWNER CONTACT EMAIL REQUIRED].
          </div>
        </section>

        {/* Section 10: Changes to this Notice */}
        <section className="p-5 sm:p-6 rounded-2xl bg-[#141414] border border-[#262626] space-y-3">
          <h2 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
            <span className="w-6 h-6 rounded-lg bg-emerald-600/20 text-emerald-400 flex items-center justify-center text-xs font-mono">10</span>
            Changes to this Privacy Notice
          </h2>
          <p>
            This Privacy Notice may be revised periodically. Any updates will be published directly on this page at <a href="https://quickibible.com" className="text-[#A3A3A3] hover:underline">https://quickibible.com</a> with an updated effective date.
          </p>
        </section>
      </div>

      {/* Footer Back Button */}
      <div className="pt-4 border-t border-[#262626] flex justify-center">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[#525252] hover:bg-[#404040] text-white text-xs font-semibold shadow-md transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Return to Quick!Bible</span>
        </button>
      </div>
    </div>
  );
};
